import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BadgeCheck,
  CircleAlert,
  Clock3,
  CreditCard,
  ShieldCheck,
  X,
} from "lucide-react";
import {
  listSettlementRecipients,
  startSettlementRecipientBinding,
} from "../../../entities/provider/api";
import { getApiErrorMessage } from "../../../shared/api/errors";
import { showToast } from "../../../shared/ui/toast";
import "./ProviderSettlementSection.css";

const recipientStatusMeta = {
  PENDING: { label: "绑定中", tone: "pending", icon: Clock3 },
  ACTIVE: { label: "已生效", tone: "success", icon: BadgeCheck },
  FAILED: { label: "绑定失败", tone: "error", icon: CircleAlert },
  DISABLED: { label: "已停用", tone: "muted", icon: CircleAlert },
};
const showAlipaySandboxLabel = import.meta.env.DEV
  || import.meta.env.VITE_ALIPAY_SANDBOX === "true";

function formatBindingTime(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function SettlementRecipientCard({ recipient }) {
  const status = String(recipient.status || "").toUpperCase();
  const meta = recipientStatusMeta[status] || {
    label: "状态处理中",
    tone: "muted",
    icon: Clock3,
  };
  const StatusIcon = meta.icon;

  return (
    <article className="provider-recipient-card">
      <div className="provider-recipient-icon">
        <span>支</span>
      </div>
      <div className="provider-recipient-info">
        <strong>支付宝结算账户</strong>
        <span>{recipient.account_masked || "账户信息同步中"}</span>
        {showAlipaySandboxLabel && recipient.channel === "ALIPAY" ? (
          <small className="provider-recipient-sandbox-note">
            当前绑定为支付宝沙箱版用户
          </small>
        ) : null}
        {recipient.bound_at ? <small>绑定于 {formatBindingTime(recipient.bound_at)}</small> : null}
      </div>
      <span className={`provider-recipient-status ${meta.tone}`}>
        <StatusIcon size={15} />
        {meta.label}
      </span>
    </article>
  );
}

function SettlementBindingDialog({ isSubmitting, onClose, onSubmit }) {
  const [confirmed, setConfirmed] = useState(false);

  return (
    <div
      className="provider-settlement-modal"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) onClose();
      }}
    >
      <section
        className="provider-settlement-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="provider-settlement-title"
      >
        <header>
          <div className="provider-settlement-dialog-icon">
            <CreditCard size={22} />
          </div>
          <button
            type="button"
            aria-label="关闭结算账户绑定"
            disabled={isSubmitting}
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </header>
        <div className="provider-settlement-dialog-content">
          <h3 id="provider-settlement-title">绑定支付宝结算账户</h3>
          <p>
            平台将根据你的实名认证主体向支付机构发起结算接收方绑定，不需要在此填写或上传账户密码。
          </p>
          <ul>
            <li><ShieldCheck size={17} />结算主体必须与实名认证信息一致</li>
            <li><Clock3 size={17} />提交后由支付机构异步处理，页面会自动更新状态</li>
            <li><CreditCard size={17} />账户生效后，订单收入才具备结算条件</li>
          </ul>
          <label className="provider-settlement-confirmation">
            <input
              type="checkbox"
              checked={confirmed}
              disabled={isSubmitting}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            <span>我确认使用当前实名认证主体绑定支付宝结算账户</span>
          </label>
        </div>
        <footer>
          <button
            className="primary-button"
            type="button"
            disabled={!confirmed || isSubmitting}
            onClick={onSubmit}
          >
            {isSubmitting ? "正在发起绑定……" : "确认绑定"}
          </button>
        </footer>
      </section>
    </div>
  );
}

export default function ProviderSettlementSection({ provider }) {
  const queryClient = useQueryClient();
  const [bindingDialogOpen, setBindingDialogOpen] = useState(false);
  const canAccessSettlement = provider.agreement_status === "ACCEPTED"
    || provider.next_action === "BIND_SETTLEMENT_RECIPIENT"
    || provider.settlement_status === "READY"
    || provider.status === "ACTIVE";
  const recipientsQuery = useQuery({
    queryKey: ["provider", "settlement-recipients"],
    queryFn: listSettlementRecipients,
    enabled: canAccessSettlement,
    refetchInterval: (query) => (
      query.state.data?.some((recipient) => recipient.status === "PENDING")
        ? 3_000
        : false
    ),
  });
  const recipients = recipientsQuery.data || [];
  const hasActiveRecipient = recipients.some((recipient) => recipient.status === "ACTIVE");
  const hasPendingRecipient = recipients.some((recipient) => recipient.status === "PENDING");

  useEffect(() => {
    if (hasActiveRecipient && provider.settlement_status !== "READY") {
      queryClient.invalidateQueries({ queryKey: ["provider", "me"] });
    }
  }, [
    hasActiveRecipient,
    provider.settlement_status,
    queryClient,
  ]);

  const bindingMutation = useMutation({
    mutationFn: () => startSettlementRecipientBinding("ALIPAY"),
    onSuccess: async () => {
      setBindingDialogOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["provider", "settlement-recipients"] }),
        queryClient.invalidateQueries({ queryKey: ["provider", "me"] }),
      ]);
      showToast("支付宝结算账户绑定已发起");
    },
    onError: (error) => {
      showToast(getApiErrorMessage(error, "结算账户绑定失败"), "error");
    },
  });

  if (!canAccessSettlement) return null;

  return (
    <section className="provider-settlement-section" aria-labelledby="provider-settlement-heading">
      <div className="provider-settlement-heading">
        <div>
          <h3 id="provider-settlement-heading">结算账户</h3>
          <p>管理用于接收设计服务收入的支付机构结算账户。</p>
        </div>
        {!hasActiveRecipient && !hasPendingRecipient && !recipientsQuery.isPending ? (
          <button
            className="primary-button"
            type="button"
            onClick={() => setBindingDialogOpen(true)}
          >
            绑定支付宝
          </button>
        ) : null}
      </div>

      {recipientsQuery.isPending ? (
        <div className="provider-recipient-query-state">正在查询结算账户……</div>
      ) : recipientsQuery.isError ? (
        <div className="provider-recipient-query-state error">
          <CircleAlert size={19} />
          <div>
            <strong>结算账户加载失败</strong>
            <span>{getApiErrorMessage(recipientsQuery.error, "请稍后重新加载")}</span>
          </div>
          <button
            className="secondary-button"
            type="button"
            onClick={() => recipientsQuery.refetch()}
          >
            重新加载
          </button>
        </div>
      ) : recipients.length ? (
        <div className="provider-recipient-list">
          {recipients.map((recipient) => (
            <SettlementRecipientCard recipient={recipient} key={recipient.id} />
          ))}
        </div>
      ) : (
        <div className="provider-recipient-empty">
          <CreditCard size={21} />
          <div>
            <strong>尚未绑定结算账户</strong>
            <span>完成绑定后，平台才能将订单收入结算至你的账户。</span>
          </div>
        </div>
      )}

      {bindingDialogOpen ? (
        <SettlementBindingDialog
          isSubmitting={bindingMutation.isPending}
          onClose={() => setBindingDialogOpen(false)}
          onSubmit={() => bindingMutation.mutate()}
        />
      ) : null}
    </section>
  );
}
